import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

/**
 * RobotBrain — a humanoid AI brain. Two metallic hemispheres separated by a
 * glowing central seam, surface stitched with luminous neural traces, and a
 * halo of orbiting circuit nodes. Pulses with scroll progress in section 5
 * (the AI / Cognitive Layer of FIVE QUANTUM).
 */
export function RobotBrain() {
  const root = useRef<THREE.Group>(null);
  const halo = useRef<THREE.Group>(null);
  const seamMat = useRef<THREE.MeshStandardMaterial>(null);
  const traceMat = useRef<THREE.LineBasicMaterial>(null);
  const { section, sectionProgress } = useScroll();

  // Generate luminous neural traces hugging the hemisphere surface
  const traceGeom = useMemo(() => {
    const positions: number[] = [];
    const r = 1.0;
    const traces = 48;
    for (let t = 0; t < traces; t++) {
      const baseTheta = Math.random() * Math.PI * 2;
      const basePhi = Math.random() * Math.PI * 0.85 + Math.PI * 0.05;
      const segments = 14 + Math.floor(Math.random() * 10);
      let theta = baseTheta;
      let phi = basePhi;
      for (let s = 0; s < segments; s++) {
        const x = r * Math.sin(phi) * Math.cos(theta);
        const y = r * Math.cos(phi);
        const z = r * Math.sin(phi) * Math.sin(theta);
        if (s > 0) {
          // Push line as segment pair
          positions.push(positions[positions.length - 3], positions[positions.length - 2], positions[positions.length - 1]);
        }
        positions.push(x, y, z);
        // Drift along surface
        theta += (Math.random() - 0.5) * 0.35;
        phi += (Math.random() - 0.5) * 0.18;
        phi = Math.max(0.05, Math.min(Math.PI - 0.05, phi));
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, []);

  // Halo of orbiting circuit nodes
  const haloNodes = useMemo(() => {
    const arr: { x: number; y: number; z: number; s: number }[] = [];
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      const r = 1.65 + (i % 3) * 0.08;
      arr.push({
        x: Math.cos(a) * r,
        y: Math.sin(a) * 0.18 + (i % 2 === 0 ? 0.05 : -0.05),
        z: Math.sin(a) * r,
        s: 0.025 + (i % 5) * 0.006,
      });
    }
    return arr;
  }, []);

  useFrame((_state, delta) => {
    const active = section < 5 ? 0 : section === 5 ? sectionProgress : 1;
    const tNow = performance.now() * 0.001;
    const pulse = 0.5 + 0.5 * Math.sin(tNow * 1.6);

    if (root.current) {
      root.current.rotation.y += delta * 0.18;
      root.current.rotation.x = Math.sin(tNow * 0.4) * 0.06;
      const s = 0.85 + active * 0.2;
      root.current.scale.setScalar(s);
    }
    if (halo.current) {
      halo.current.rotation.y -= delta * 0.45;
      halo.current.rotation.x = Math.PI * 0.5;
    }
    if (seamMat.current) {
      seamMat.current.emissiveIntensity = 1.4 + pulse * 1.2 + active * 0.8;
    }
    if (traceMat.current) {
      traceMat.current.opacity = 0.25 + active * 0.55 + pulse * 0.2;
    }
  });

  return (
    <group ref={root}>
      {/* Left hemisphere */}
      <mesh position={[-0.04, 0, 0]} castShadow>
        <sphereGeometry args={[1, 64, 48, 0, Math.PI]} />
        <meshStandardMaterial
          color="#1a1c22"
          metalness={0.95}
          roughness={0.32}
          envMapIntensity={1.2}
        />
      </mesh>
      {/* Right hemisphere */}
      <mesh position={[0.04, 0, 0]} rotation={[0, Math.PI, 0]} castShadow>
        <sphereGeometry args={[1, 64, 48, 0, Math.PI]} />
        <meshStandardMaterial
          color="#1a1c22"
          metalness={0.95}
          roughness={0.32}
          envMapIntensity={1.2}
        />
      </mesh>

      {/* Inner core — glowing brain matter visible through the seam */}
      <mesh>
        <sphereGeometry args={[0.92, 32, 24]} />
        <meshStandardMaterial
          color="#2a1604"
          emissive="#ff8a2a"
          emissiveIntensity={1.6}
          roughness={0.7}
          metalness={0}
          toneMapped={false}
        />
      </mesh>

      {/* Glowing central seam — a thin disc disk between hemispheres */}
      <mesh rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.985, 0.018, 24, 128]} />
        <meshStandardMaterial
          ref={seamMat}
          color="#1c0e02"
          emissive="#ffb14a"
          emissiveIntensity={2.2}
          metalness={0}
          roughness={0.5}
          toneMapped={false}
        />
      </mesh>

      {/* Surface neural traces */}
      <lineSegments geometry={traceGeom}>
        <lineBasicMaterial
          ref={traceMat}
          color="#7fb8ff"
          transparent
          opacity={0.5}
          depthWrite={false}
          toneMapped={false}
        />
      </lineSegments>

      {/* Cross-hemisphere stitching ribs (visual "bolts") */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[0, Math.sin(a) * 0.95, Math.cos(a) * 0.95]}
            rotation={[0, 0, Math.PI / 2]}
          >
            <cylinderGeometry args={[0.022, 0.022, 0.18, 12]} />
            <meshStandardMaterial color="#3a2a10" metalness={1} roughness={0.4} />
          </mesh>
        );
      })}

      {/* Cranial spine — central ridge running over the top */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.99, 0.045, 16, 80, Math.PI]} />
        <meshStandardMaterial
          color="#22232a"
          metalness={1}
          roughness={0.25}
          emissive="#1a1208"
          emissiveIntensity={0.4}
        />
      </mesh>

      {/* Optical/sensor pods on the temples */}
      <mesh position={[1.0, 0.1, 0]}>
        <sphereGeometry args={[0.08, 24, 16]} />
        <meshStandardMaterial color="#0a0a0a" metalness={0.9} roughness={0.15} emissive="#ff5a1f" emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
      <mesh position={[-1.0, 0.1, 0]}>
        <sphereGeometry args={[0.08, 24, 16]} />
        <meshStandardMaterial color="#0a0a0a" metalness={0.9} roughness={0.15} emissive="#ff5a1f" emissiveIntensity={1.6} toneMapped={false} />
      </mesh>

      {/* Halo of circuit nodes */}
      <group ref={halo}>
        {haloNodes.map((n, i) => (
          <mesh key={i} position={[n.x, n.y, n.z]}>
            <icosahedronGeometry args={[n.s, 0]} />
            <meshStandardMaterial
              color="#0a0a0a"
              metalness={1}
              roughness={0.2}
              emissive="#7fb8ff"
              emissiveIntensity={2.4}
              toneMapped={false}
            />
          </mesh>
        ))}
      </group>

      {/* Localized warm fill from inside the head */}
      <pointLight position={[0, 0, 0]} intensity={1.6} color="#ffb14a" distance={2.6} decay={2} />
    </group>
  );
}
